import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Eye, EyeOff, Moon, Sun, LogIn, Info, Copy, ChevronDown, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeProvider";
import { apiErrorMessage } from "@/lib/api";

// Cuentas demo por rol (para ver las distintas visualizaciones según el perfil).
const DEMO_ACCOUNTS = [
  { label: "Director-Admin General (Admin)", email: "admin@sofi.com", password: "Sofi2025!" },
  { label: "Director of Sales (Jefa de ventas)", email: "ventas@sofi.com", password: "demo1234" },
  { label: "Directora de Operaciones", email: "operaciones@sofi.com", password: "demo1234" },
  { label: "Travel Specialist (Agente)", email: "especialista@sofi.com", password: "demo1234" },
  { label: "Agente de Reservas", email: "reservas@sofi.com", password: "demo1234" },
  { label: "Traveler Support", email: "support@sofi.com", password: "demo1234" },
  { label: "Agente de Facturación", email: "facturacion@sofi.com", password: "demo1234" },
  { label: "Contable", email: "contable@sofi.com", password: "demo1234" },
  { label: "Administración", email: "administracion@sofi.com", password: "demo1234" },
  { label: "Socio-Director", email: "socio@sofi.com", password: "demo1234" },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { dark, setDark } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  // El panel de acceso demo sólo se muestra en entornos de preview/dev.
  // En producción (cualquier hostname que no sea `.preview.emergentagent.com` /
  // localhost) queda oculto por defecto — así no expone credenciales de test
  // a los usuarios reales que llegan a `sofi.viajadverdad.com`.
  const isDevEnv = typeof window !== "undefined" && (
    window.location.hostname.endsWith(".preview.emergentagent.com") ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );

  React.useEffect(() => {
    if (isAuthenticated) navigate("/app", { replace: true });
  }, [isAuthenticated, navigate]);

  const doLogin = async (em, pw) => {
    setSubmitting(true);
    try {
      const data = await login(em.trim(), pw);
      toast.success(`Bienvenido/a, ${data.user.full_name}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo iniciar sesión"));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Introduce email y contraseña");
      return;
    }
    await doLogin(email, password);
  };

  const copyText = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg || "Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const quickLogin = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
    doLogin(acc.email, acc.password);
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background bg-ocean-mist px-4">
      <div className="absolute inset-0 bg-noise opacity-[0.6]" aria-hidden="true" />
      <Button
        variant="ghost" size="icon"
        className="absolute right-4 top-4 h-9 w-9"
        onClick={() => setDark((d) => !d)}
        data-testid="login-theme-toggle"
        aria-label="Cambiar tema claro/oscuro"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <div className="relative w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg">
            <Compass className="h-7 w-7" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">SOFI 2.0</h1>
          <p className="text-sm text-muted-foreground">Plataforma de gestión · ERP · CRM</p>
        </div>

        <Card className="border-border/70 shadow-xl backdrop-blur">
          <CardContent className="p-6">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" autoComplete="username"
                  placeholder="tu@empresa.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPw ? "text" : "password"} autoComplete="current-password"
                    placeholder="••••••••"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="login-toggle-password"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button">
                <LogIn className="mr-2 h-4 w-4" />
                {submitting ? "Entrando…" : "Iniciar sesión"}
              </Button>
            </form>

            {isDevEnv && (
            <div className="mt-5 rounded-lg border border-dashed border-border bg-muted/40 p-3" data-testid="login-demo-panel">
              <button
                type="button"
                onClick={() => setShowDemo((s) => !s)}
                className="flex w-full items-center justify-between text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
                data-testid="login-demo-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" /> Entrar como… (demo por rol)
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showDemo ? "rotate-180" : ""}`} />
              </button>

              {showDemo && (
                <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="login-demo-list">
                  {DEMO_ACCOUNTS.map((acc) => (
                    <div
                      key={acc.email}
                      className="flex items-center gap-2 rounded-md border border-border/60 bg-background p-2"
                      data-testid={`login-demo-row-${acc.email}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-semibold text-foreground">{acc.label}</p>
                        <p className="truncate font-mono text-[11px] text-muted-foreground">
                          {acc.email} · {acc.password}
                        </p>
                      </div>
                      <Button
                        type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                        onClick={() => copyText(`${acc.email}  ${acc.password}`, `Copiado: ${acc.email}`)}
                        aria-label="Copiar credenciales"
                        data-testid={`login-demo-copy-${acc.email}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button" variant="secondary" size="sm" className="h-7 shrink-0 px-2 text-xs"
                        disabled={submitting}
                        onClick={() => quickLogin(acc)}
                        data-testid={`login-demo-enter-${acc.email}`}
                      >
                        Entrar
                      </Button>
                    </div>
                  ))}
                  <p className="pt-1 text-[11px] text-muted-foreground">
                    Contraseña común demo: <span className="font-mono text-foreground">demo1234</span> · Admin: <span className="font-mono text-foreground">Sofi2025!</span>
                  </p>
                </div>
              )}

              {!showDemo && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Info className="h-3 w-3" /> 10 perfiles disponibles para probar las distintas vistas.
                </p>
              )}
            </div>
            )}
          </CardContent>
        </Card>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          ¿Olvidaste tu contraseña? Contáctalo con el Director-Administrador para resetearla.
        </p>
      </div>
    </div>
  );
}
